import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { ChatMessage, PropertyFilters } from '../shared/dto';
import { ResultsService } from './results.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private userService = inject(UserService);
  private resultsService = inject(ResultsService);

  private websocket: WebSocket | null = null;
  private clientId = this.userService.clientId;
  private readonly _isTyping = signal<boolean>(false);
  // Single-flight guard for load_more_properties requests.
  private readonly _isLoadingMore = signal<boolean>(false);
  private readonly _propertiesDepleted = signal<boolean>(false);
  private readonly _isCancelled = signal<boolean>(false);
  private readonly _thinkingMessage = signal<string>('');
  private readonly _error = signal<string>('');
  private readonly _initializationFailed = signal<boolean>(false);
  private currentSearchId: string | null = null;
  private loadMoreRequestsCount = 0;

  public readonly isTyping = this._isTyping.asReadonly();
  public readonly isLoadingMore = this._isLoadingMore.asReadonly();
  public readonly isCancelled = this._isCancelled.asReadonly();
  public readonly propertiesDepleted = this._propertiesDepleted.asReadonly();
  public readonly initalizationFailed = this._initializationFailed.asReadonly();
  public readonly error = this._error.asReadonly();
  public readonly thinkingMessage = this._thinkingMessage.asReadonly();

  constructor() {
    this.initWebsocketConnection().catch((error) => {
      this._initializationFailed.set(true);
    });
  }

  private async initWebsocketConnection(): Promise<void> {
    await this.connectWebSocket();
    // Send initial ping to keep connection alive
    this.websocket?.send(JSON.stringify({ type: 'ping' }));
  }

  private async connectWebSocket(): Promise<void> {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      return;
    }

    return new Promise((resolve, reject) => {
      this.websocket = new WebSocket(
        `${environment.apiWs}/${environment.apiUrl}/search/${this.clientId()}?token=${environment.accessToken}`,
      );

      this.websocket.onopen = (): void => {
        resolve();
      };

      this.websocket.onmessage = (event): void => {
        this.handleWebSocketMessage(event);
      };

      this.websocket.onerror = (error): void => {
        this._isTyping.set(false);
        this._isLoadingMore.set(false);
        reject(error);
      };

      this.websocket.onclose = (): void => {
        this._isLoadingMore.set(false);
        this._isTyping.set(false);
        this.websocket = null;
      };
    });
  }

  private handleWebSocketMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      let message: ChatMessage | undefined = undefined;

      if (data.messageType === 'debug' && this.currentSearchId) {
        // this.debugService.addDebugInfo(this.currentSearchId, data.debug_info);
      } else if (data.messageType === 'thinking') {
        // Progress messages - update thinking message
        this._thinkingMessage.set(`${data.message}`);
        this._isTyping.set(true);
      } else if (data.messageType === 'message') {
        // Result messages - show as chat messages

        if (data.type === 'property_extracted') {
          // Keep typing = true as search continues
          this._isTyping.set(true);
        } else if (data.type === 'completed') {
          // Search finished successfully
          this._isTyping.set(false);
          this._isLoadingMore.set(false);
          this._thinkingMessage.set('');

          if (this.resultsService.listings().length === 0) {
            this._propertiesDepleted.set(true);
          }
        } else if (data.type === 'depleted') {
          this._propertiesDepleted.set(true);
          this._isTyping.set(false);
          this._isLoadingMore.set(false);
          this._thinkingMessage.set('');
        } else if (data.type === 'error') {
          this._error.set(data.message);
          this._isTyping.set(false);
          this._isLoadingMore.set(false);
          this._thinkingMessage.set('');
        }
      }

      if (data.data) {
        this.resultsService.addResult(data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  public requestMoreProperties(): void {
    // Prevent spamming multiple load_more_properties while one is still in flight.
    if (this._isLoadingMore()) {
      return;
    }

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      // Mark in-flight before sending request.
      this._isLoadingMore.set(true);
      this.loadMoreRequestsCount += 1;
      const message = {
        type: 'load_more_properties',
      };
      this.websocket.send(JSON.stringify(message));
    } else {
      this._isLoadingMore.set(false);
    }
  }

  public async search(propertyFilters: PropertyFilters): Promise<void> {
    try {
      this._error.set("");
      this._propertiesDepleted.set(false);
      this._isLoadingMore.set(true);
      this._isCancelled.set(false);
      this.loadMoreRequestsCount = 0;

      // Show typing indicator and clear previous results
      this._isTyping.set(true);

      await this.connectWebSocket();

      // Send message via WebSocket
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        const message = {
          type: 'start_search',
          prompt: '',
          property_filters: propertyFilters,
        };
        this.websocket.send(JSON.stringify(message));
      } else {
        throw new Error('WebSocket connection not available');
      }
    } catch (error) {
      console.error(error);
      this._isTyping.set(false);
      this._isLoadingMore.set(false);
    }
  }

  public stopSearch(): void {
    // Send stop_search message via WebSocket
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'stop_search',
      };
      this.websocket.send(JSON.stringify(message));
    }
    this._isTyping.set(false);
    this._isLoadingMore.set(false);
    this._thinkingMessage.set('');
    this._isCancelled.set(true);
  }
}
